import { useState } from "react";
import { motion } from "framer-motion";
import { Toaster } from "sonner";
import { ContactSalesForm } from "../components/contact/ContactSalesForm";
import { CONTACT_SALES_TOPICS } from "../data/contactSalesContent";

export default function ContactSalesPage() {
    const [form, setForm] = useState({ reason: CONTACT_SALES_TOPICS[0].id });

    return (
        <div className="pt-28 lg:pt-32" data-testid="contact-sales-page">
            <Toaster position="top-right" theme="dark" richColors />

            {/* Hero */}
            <section className="relative overflow-hidden py-12 lg:py-16">
                <div className="absolute inset-0 radial-glow pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-7">
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-3">
                            // contact sales · one business day reply
                        </p>
                        <h1
                            className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.04]"
                            data-testid="contact-sales-title"
                        >
                            Talk to a human
                            <br />
                            <span className="gradient-text">
                                who knows the product.
                            </span>
                        </h1>
                    </div>
                    <p className="lg:col-span-5 text-base sm:text-lg text-zinc-400 leading-relaxed font-light">
                        Sales at ApexBrain is staffed by sales engineers who
                        also write code. No discovery loops, no second-tier
                        gatekeeping — straight to a working answer.
                    </p>
                </div>
            </section>

            {/* Topic + Form */}
            <section className="relative pb-16">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Topics */}
                    <aside
                        className="lg:col-span-5 space-y-3 lg:max-h-[calc(100vh-12rem)] lg:overflow-y-auto lg:pr-2"
                        data-testid="contact-topics"
                    >
                        <p className="font-code text-[10px] uppercase tracking-[0.2em] text-amber-glow mb-2">
                            // what do you need?
                        </p>
                        {CONTACT_SALES_TOPICS.map((t, i) => {
                            const active = form.reason === t.id;
                            return (
                                <motion.button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setForm({ reason: t.id })}
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.35, delay: i * 0.05 }}
                                    data-testid={`contact-topic-${t.id}`}
                                    className={`w-full text-left rounded-2xl border p-5 transition ${
                                        active
                                            ? `${t.chip} bg-white/[0.05]`
                                            : "bg-obsidian-200/70 border-white/[0.06] hover:border-white/15"
                                    }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <span
                                            className={`inline-flex items-center justify-center w-10 h-10 rounded-lg border ${t.chip}`}
                                        >
                                            <t.icon
                                                className={`w-4 h-4 ${t.accent}`}
                                            />
                                        </span>
                                        <div className="flex-1">
                                            <div className="font-outfit text-base font-semibold text-white">
                                                {t.title}
                                            </div>
                                            <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
                                                {t.body}
                                            </p>
                                        </div>
                                        <span
                                            className={`mt-1.5 w-2 h-2 rounded-full ${active ? "bg-electric animate-pulse-glow" : "bg-zinc-700"}`}
                                        />
                                    </div>
                                </motion.button>
                            );
                        })}
                    </aside>

                    <ContactSalesForm
                        className="lg:col-span-7 rounded-2xl border border-electric/25 bg-gradient-to-br from-obsidian-100 to-obsidian-300 p-7 sm:p-9 relative overflow-hidden h-fit"
                        defaultReason={form.reason}
                        onReasonChange={(reason) => setForm({ reason })}
                    />
                </div>
            </section>
        </div>
    );
}
